import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { metenApi } from "../../services/metenApi";
import {
  MAX_COMPARED_ALTERNATIVES, comparisonAlternatives, comparisonRequest, comparesVirtualNetBilling, studyBatteryName,
  asStudyBattery,
} from "../../components/config/batteryComparison";
import { agreedPower } from "../../components/config/virtualNetBilling";

const POLL_MS = 3000;
const CHECK_DELAY_MS = 600;
const ACTIVE = new Set(["pending", "running"]);

const number = (value, digits = 0) => (value === null || value === undefined || !Number.isFinite(Number(value))
  ? "—" : Number(value).toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits }));
const money = (value) => (number(value) === "—" ? "—" : `${number(value)} €`);
const signedMoney = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || !Number.isFinite(n)) return "—";
  if (Math.abs(n) < 0.5) return "0 €";
  return `${n > 0 ? "+" : "−"}${number(Math.abs(n))} €`;
};

/** The server's message in the screen's language. */
const say = (message, isGreek) => (message && typeof message === "object"
  ? (isGreek ? message.el : message.en) || message.en || message.el || ""
  : String(message || ""));

function errorDetail(error) {
  try {
    return JSON.parse(error?.responseText || error?.message || "")?.detail;
  } catch {
    return null;
  }
}

/**
 * Battery comparison (Behind-the-Meter). Every column is a full run of the
 * last completed study with only the battery changed; the study battery runs
 * first and must reproduce the report. The batteries are the ones selected on
 * the BESS page; the study battery is the one marked as such there.
 */
export default function BatteryComparisonCard({
  isGreek, projectId, config, commitConfig, disabled, lockedTitle, stale, studyRunning, studyCompletedAt, onOpenBess, sx,
}) {
  const tr = (el, en) => (isGreek ? el : en);
  const studyName = studyBatteryName(config);
  const settings = useMemo(() => config?.batteryComparison || {}, [config?.batteryComparison]);
  const alternatives = useMemo(() => comparisonAlternatives(config), [config]);
  const body = useMemo(() => comparisonRequest(config), [config]);
  const vnb = useMemo(() => comparesVirtualNetBilling(config), [config]);
  const bodyKey = useMemo(() => JSON.stringify(body), [body]);

  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState(null);
  // The card stays small: the choices and the results open in their own windows.
  const [setupOpen, setSetupOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const poll = useRef(null);

  const running = ACTIVE.has(row?.status);

  const load = useCallback(async () => {
    if (!projectId) return null;
    try {
      const latest = await metenApi.getBatteryComparison(String(projectId));
      setRow(latest);
      return latest;
    } catch {
      return null;
    }
  }, [projectId]);

  useEffect(() => { setRow(null); setCheck(null); setFeedback(null); load(); }, [load]);

  useEffect(() => {
    if (!running) return undefined;
    poll.current = setInterval(load, POLL_MS);
    return () => clearInterval(poll.current);
  }, [running, load]);

  useEffect(() => {
    if (!setupOpen || !projectId || disabled || running || !alternatives.selected.length) {
      setCheck(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const out = await metenApi.checkBatteryComparison(String(projectId), JSON.parse(bodyKey));
        if (!cancelled) setCheck(out);
      } catch (error) {
        if (!cancelled) setCheck({ problems: [{ code: "check", en: error?.message, el: error?.message }], warnings: [] });
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, CHECK_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // A finished study is a new basis for the comparison: check again against it.
  }, [setupOpen, projectId, disabled, running, bodyKey, alternatives.selected.length, studyCompletedAt, stale]);

  // A message about the previous study ("run the simulation") no longer applies.
  useEffect(() => { setFeedback(null); }, [studyCompletedAt]);

  const updateSettings = (patch) => commitConfig((current) => ({
    ...current, batteryComparison: { ...(current?.batteryComparison || {}), ...patch },
  }));
  const setPerBattery = (field, name, value) => updateSettings({
    [field]: { ...(settings[field] || {}), [name]: value },
  });
  const toggle = (name, on) => {
    const included = new Set(alternatives.selected);
    if (on) included.add(name); else included.delete(name);
    updateSettings({ include: alternatives.all.filter((n) => included.has(n)) });
  };

  const start = async () => {
    setBusy("start");
    setFeedback(null);
    try {
      await metenApi.startBatteryComparison(String(projectId), body);
      setSetupOpen(false);
      await load();
    } catch (error) {
      const detail = errorDetail(error);
      if (detail?.problems) setCheck({ problems: detail.problems, warnings: detail.warnings || [] });
      setFeedback({ error: detail?.message || say(detail?.problems?.[0], isGreek) || (typeof detail === "string" ? detail : error?.message) });
    } finally {
      setBusy("");
    }
  };
  const cancel = async () => {
    setBusy("cancel");
    try {
      await metenApi.cancelBatteryComparison(String(projectId));
      await load();
    } finally {
      setBusy("");
    }
  };
  const download = async () => {
    setBusy("pdf");
    setFeedback(null);
    try {
      const { blob, filename } = await metenApi.downloadBatteryComparison(String(projectId), { language: isGreek ? "el" : "en" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `battery_comparison_${projectId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setFeedback({ error: error?.message || tr("Η λήψη απέτυχε.", "The download failed.") });
    } finally {
      setBusy("");
    }
  };
  const adopt = (scenario) => {
    setResultsOpen(false);
    commitConfig((current) => asStudyBattery(current, scenario.battery, {
      count: scenario.count, price: settings.prices?.[scenario.battery],
    }));
    setFeedback({ success: tr(
      `Η ${scenario.battery} είναι τώρα η μπαταρία της μελέτης. Τρέξτε την προσομοίωση για να ενημερωθούν οι αναφορές.`,
      `${scenario.battery} is now the study battery. Run the simulation to update the reports.`) });
  };

  const problems = check?.problems || [];
  const warnings = check?.warnings || [];
  const plan = check?.plan;
  const catalogue = Object.fromEntries((plan?.batteries || []).map((b) => [b.battery, b]));
  const canStart = Boolean(projectId) && !disabled && !stale && !studyRunning && !running && !checking
    && !busy && alternatives.selected.length > 0 && check && problems.length === 0;

  const result = row?.status === "completed" ? row.result : (running ? row?.result : null);
  // A comparison made before the latest study run describes the previous
  // study: it is not shown at all, and neither is its PDF.
  const comparedAt = Date.parse(row?.created_at || "");
  const studyAt = Date.parse(studyCompletedAt || "");
  const outdated = !running && Number.isFinite(comparedAt) && Number.isFinite(studyAt) && comparedAt < studyAt;
  // With a single battery there is nothing to compare: only the (greyed) button shows.
  const hasResults = row?.status === "completed" && !outdated && (result?.scenarios || []).length > 0
    && alternatives.all.length > 0;
  const system = (result?.scenarios || []).filter((s) => s.kind === "system");
  const alone = (result?.scenarios || []).filter((s) => s.kind === "battery_only");
  const studyNpv = system.find((s) => s.is_study)?.headline?.npv_eur;
  const best = result?.best || {};
  // Under virtual net billing the study's "self-sufficiency" is the netted share
  // of the supplies' consumption, and is named so, as in the offer.
  const coverageLabel = result?.virtual_net_billing
    ? tr("Κάλυψη της κατανάλωσης", "Share of consumption netted") : tr("Αυτάρκεια", "Self-sufficiency");
  // The supplies' agreed power stays on the screen (it never reaches the
  // backend or a document): a compared battery above it is said here only.
  const agreed = vnb ? agreedPower(config?.btm?.virtualNetBilling) : null;
  const agreedWarnings = agreed?.complete && plan ? (plan.batteries || [])
    .filter((b) => !b.is_study && Number(b.power_kw) > agreed.totalKva + 1e-9)
    .map((b) => tr(
      `Η ισχύς της ${b.battery} (${number(b.power_kw, 1)} kW) ξεπερνά τη συνολική συμφωνημένη ισχύ των παροχών (${number(agreed.totalKva, 1)} kVA). Με διακριτό αντιστροφέα, ο αντιστροφέας της μπαταρίας δεν επιτρέπεται να την ξεπερνά χωρίς πιστοποιημένο περιορισμό ισχύος (ΚΥΑ άρθρο 8 παρ. 2).`,
      `${b.battery} (${number(b.power_kw, 1)} kW) exceeds the supplies' total agreed power (${number(agreed.totalKva, 1)} kVA). With a separate inverter, the battery inverter may not exceed it without a certified power limit (ΚΥΑ art. 8 par. 2).`)) : [];

  const progressLabel = () => {
    const d = row?.detail || {};
    if (row?.status === "pending") return tr("Σε αναμονή…", "Queued…");
    if (row?.phase === "preflight") return tr("Έλεγχος των ρυθμίσεων όλων των μπαταριών…", "Checking every battery's configuration…");
    if (row?.phase === "timeseries") return tr("Προετοιμασία τιμών και χρονοσειρών…", "Preparing prices and time series…");
    if (!d.index) return tr("Σε εξέλιξη…", "Running…");
    const which = `${d.battery}${d.kind === "battery_only" ? tr(" (μόνο μπαταρία)", " (battery only)") : ""}`;
    const step = d.step === "optimization" && d.day_index
      ? tr(` · βελτιστοποίηση, ημέρα ${d.day_index}/${d.total_days}`, ` · optimization, day ${d.day_index}/${d.total_days}`)
      : ["reports", "pv_financial_metrics"].includes(d.step) ? tr(" · οικονομική ανάλυση", " · financial analysis")
        : d.step === "pv_generation" ? tr(" · παραγωγή Φ/Β", " · PV generation") : "";
    return `${tr("Προσομοίωση", "Simulation")} ${d.index}/${d.total}: ${which}${step}`;
  };

  const cell = (s, value, code) => (
    <TableCell key={s.key} align="right" sx={{ whiteSpace: "nowrap", fontWeight: best[code] === s.key ? 700 : undefined,
      color: best[code] === s.key ? "success.main" : undefined, bgcolor: s.is_study ? "action.hover" : undefined }}>
      {value}
    </TableCell>
  );
  const head = (items) => (
    <TableHead>
      <TableRow>
        <TableCell />
        {items.map((s) => (
          <TableCell key={s.key} align="right" sx={{ fontWeight: 600, bgcolor: s.is_study ? "action.hover" : undefined }}>
            <span data-no-translate>{s.count > 1 ? `${s.count} × ` : ""}{s.battery}</span>
            {s.is_study ? <Typography variant="caption" component="div" color="text.secondary">{tr("μελέτη", "study")}</Typography> : null}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const fullWidth = { width: "100%", boxSizing: "border-box" };
  const included = alternatives.selected.length;
  const canOpen = Boolean(projectId) && !disabled && !studyRunning && alternatives.all.length > 0;

  return (
    <Box sx={sx} data-testid="battery-comparison-card">
      <Box sx={{ width: "100%" }}>
        <Typography variant="subtitle2" component="h3">{tr("Σύγκριση μπαταριών", "Battery comparison")}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {tr("Ίδιο έργο, ίδιες ρυθμίσεις· αλλάζει μόνο η μπαταρία.", "Same project, same settings; only the battery changes.")}
        </Typography>
        {alternatives.all.length ? (
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            {tr("Μπαταρία της μελέτης", "Study battery")}: <strong data-no-translate>{studyName}</strong>
            {" · "}{tr(`${included} ακόμη για σύγκριση`, `${included} more to compare`)}
          </Typography>
        ) : (
          <Typography variant="caption" component="div" sx={{ color: "text.secondary", mt: 0.25 }}>
            {tr("Για σύγκριση, επιλέξτε κι άλλες μπαταρίες στη σελίδα BESS.", "To compare, select more batteries on the BESS page.")}
            {onOpenBess ? (
              <Button size="small" onClick={onOpenBess} sx={{ ml: 0.5, minHeight: 0, py: 0 }}>{tr("Σελίδα BESS", "BESS page")}</Button>
            ) : null}
          </Typography>
        )}
      </Box>

      {running ? (
        <Box sx={{ width: "100%" }}>
          <LinearProgress variant={row?.status === "pending" ? "indeterminate" : "determinate"} value={Number(row?.progress) || 0} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ flex: 1 }} data-no-translate>{progressLabel()}</Typography>
            <Typography variant="body2" color="text.secondary">{Math.floor(Number(row?.progress) || 0)}%</Typography>
            <Button size="small" onClick={cancel} disabled={busy === "cancel"}>{tr("Ακύρωση", "Cancel")}</Button>
          </Stack>
        </Box>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap title={disabled ? lockedTitle : undefined}>
          <Button variant="contained" disabled={!canOpen} onClick={() => { setFeedback(null); setSetupOpen(true); }}
            startIcon={<CompareArrowsIcon />}>
            {tr("Σύγκριση μπαταριών", "Compare batteries")}
          </Button>
          {hasResults ? (
            <>
              <Button variant="outlined" onClick={() => setResultsOpen(true)} startIcon={<TableChartOutlinedIcon />}>
                {tr("Αποτελέσματα", "Results")}
              </Button>
              <Button variant="outlined" onClick={download} disabled={Boolean(busy)}
                startIcon={busy === "pdf" ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}>
                {tr("Λήψη συγκριτικού PDF", "Download comparison PDF")}
              </Button>
            </>
          ) : null}
        </Stack>
      )}

      {row?.status === "failed" && row?.error && !outdated ? (
        <Alert severity="error" sx={fullWidth}>{say(row.error, isGreek)}</Alert>
      ) : null}
      {!setupOpen && (feedback?.error || feedback?.success) ? (
        <Alert severity={feedback.error ? "error" : "success"} sx={fullWidth}>{feedback.error || feedback.success}</Alert>
      ) : null}

      <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{tr("Σύγκριση μπαταριών", "Battery comparison")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {tr("Κάθε μπαταρία προσομοιώνεται πλήρως όπως η μελέτη, και η μπαταρία της μελέτης πρέπει να δώσει τα ίδια αποτελέσματα με την αναφορά.",
                "Each battery is fully simulated like the study, and the study battery must reproduce the report's results.")}
            </Typography>
            <Typography variant="body2">
              {tr("Μπαταρία της μελέτης", "Study battery")}: <strong data-no-translate>{studyName}</strong>
            </Typography>
            {alternatives.all.length > MAX_COMPARED_ALTERNATIVES ? (
              <Typography variant="caption" color="text.secondary">
                {tr(`Επιλέξτε έως ${MAX_COMPARED_ALTERNATIVES} μπαταρίες για τη σύγκριση.`, `Choose up to ${MAX_COMPARED_ALTERNATIVES} batteries for the comparison.`)}
              </Typography>
            ) : null}
            {alternatives.all.map((name) => {
              const on = alternatives.selected.includes(name);
              const info = catalogue[name];
              const catalogueHint = info?.price_source === "simplified" && info?.price_eur != null
                ? tr(`Κενό: ${money(info.price_eur)} (€/kWh × kWh)`, `Blank: ${money(info.price_eur)} (€/kWh × kWh)`)
                : info?.price_source === "catalogue" && info?.price_eur != null
                ? tr(`Κενό: ${money(info.price_eur)} (κατάλογος)`, `Blank: ${money(info.price_eur)} (catalogue)`)
                : tr("Κενό: τιμή καταλόγου", "Blank: catalogue price");
              return (
                <Box key={name} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 96px 210px" }, gap: 1, alignItems: "start" }}>
                  <FormControlLabel sx={{ m: 0, minWidth: 0, pt: 0.5 }}
                    control={<Checkbox size="small" checked={on}
                      disabled={!on && alternatives.selected.length >= MAX_COMPARED_ALTERNATIVES}
                      onChange={(e) => toggle(name, e.target.checked)} />}
                    label={<Typography variant="body2" data-no-translate sx={{ overflowWrap: "anywhere" }}>{name}</Typography>} />
                  <TextField size="small" type="number" label={tr("Πλήθος", "Count")} disabled={!on}
                    value={settings.counts?.[name] ?? alternatives.counts[name] ?? 1}
                    inputProps={{ min: 1, step: 1 }}
                    onChange={(e) => setPerBattery("counts", name, e.target.value === "" ? null : Math.max(1, Math.round(Number(e.target.value))))} />
                  <TextField size="small" type="number" label={tr("Τιμή μπαταρίας (€)", "Battery price (€)")} disabled={!on}
                    value={settings.prices?.[name] ?? ""} placeholder={info?.price_eur != null ? String(Math.round(info.price_eur)) : ""}
                    helperText={catalogueHint} inputProps={{ min: 0, step: 100 }} InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPerBattery("prices", name, e.target.value === "" ? null : Number(e.target.value))} />
                </Box>
              );
            })}
            {vnb ? (
              <Typography variant="caption" color="text.secondary" data-testid="battery-comparison-vnb-note">
                {tr("Εικονικός συμψηφισμός: ίδιες παροχές, ποσοστά, επίπεδα τάσης και τιμές ανά παροχή. Η μπαταρία είναι μέρος του σταθμού και δεν φορτίζει από το δίκτυο (ΚΥΑ άρθρο 8), γι' αυτό δεν συγκρίνεται μόνη της, χωρίς Φ/Β.",
                  "Virtual net billing: the same supplies, shares, voltage levels and prices per supply. The battery is part of the station and does not charge from the grid (ΚΥΑ art. 8), so it is not compared on its own, without PV.")}
              </Typography>
            ) : (
              <FormControlLabel sx={{ m: 0 }}
                control={<Checkbox size="small" checked={Boolean(settings.batteryOnly)} onChange={(e) => updateSettings({ batteryOnly: e.target.checked })} />}
                label={<Typography variant="body2">{tr("Και κάθε μπαταρία μόνη της, χωρίς Φ/Β (μία προσομοίωση ακόμη ανά μπαταρία)",
                  "Also each battery on its own, without PV (one more simulation per battery)")}</Typography>} />
            )}
            <Typography variant="caption" color="text.secondary">
              {tr("Οι άλλες μπαταρίες λειτουργούν με τις ρυθμίσεις λειτουργίας της μπαταρίας της μελέτης (SoC, κύκλοι ανά ημέρα).",
                "The other batteries run with the study battery's operating settings (SoC, cycles per day).")}
            </Typography>
            {stale ? <Alert severity="warning">
              {tr("Οι ρυθμίσεις άλλαξαν μετά την τελευταία προσομοίωση. Τρέξτε πρώτα την προσομοίωση, ώστε η σύγκριση να γίνει πάνω στις τρέχουσες ρυθμίσεις.",
                "The settings changed after the last simulation. Run the simulation first, so the comparison uses the current settings.")}
            </Alert> : null}
            {problems.map((p, i) => <Alert key={`p${i}`} severity="error">{say(p, isGreek)}</Alert>)}
            {warnings.map((w, i) => <Alert key={`w${i}`} severity="warning">{say(w, isGreek)}</Alert>)}
            {agreedWarnings.map((text, i) => <Alert key={`a${i}`} severity="warning" data-no-translate>{text}</Alert>)}
            {feedback?.error ? <Alert severity="error">{feedback.error}</Alert> : null}
            {plan && !problems.length ? (
              <Typography variant="caption" color="text.secondary">
                {tr(`Θα γίνουν ${plan.simulations} προσομοιώσεις, πρώτα της μπαταρίας της μελέτης.`, `${plan.simulations} simulations will run, the study battery first.`)}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupOpen(false)}>{tr("Κλείσιμο", "Close")}</Button>
          <Button variant="contained" disabled={!canStart} onClick={start}
            startIcon={busy === "start" || checking ? <CircularProgress color="inherit" size={18} /> : <CompareArrowsIcon />}>
            {tr("Έναρξη σύγκρισης", "Start comparison")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resultsOpen && hasResults} onClose={() => setResultsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{tr("Αποτελέσματα σύγκρισης μπαταριών", "Battery comparison results")}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" aria-label={tr("Αποτελέσματα σύγκρισης μπαταριών", "Battery comparison results")}>
              {head(system)}
              <TableBody>
                <TableRow><TableCell>{tr("Τιμή μπαταρίας", "Battery price")}</TableCell>{system.map((s) => cell(s, money(s.price_eur)))}</TableRow>
                <TableRow><TableCell>{tr("Συνολική επένδυση", "Total investment")}</TableCell>{system.map((s) => cell(s, money(s.headline?.capex_eur)))}</TableRow>
                <TableRow><TableCell>NPV</TableCell>{system.map((s) => cell(s, money(s.headline?.npv_eur), "npv"))}</TableRow>
                <TableRow><TableCell>{tr("Διαφορά NPV από τη μελέτη", "NPV vs the study")}</TableCell>{system.map((s) => cell(s, s.is_study ? "—" : signedMoney((s.headline?.npv_eur ?? NaN) - (studyNpv ?? NaN))))}</TableRow>
                <TableRow><TableCell>IRR</TableCell>{system.map((s) => cell(s, s.headline?.irr_pct == null ? "—" : `${number(s.headline.irr_pct, 1)}%`))}</TableRow>
                <TableRow><TableCell>{tr("Προεξοφλημένη απόσβεση", "Discounted payback")}</TableCell>{system.map((s) => cell(s, s.headline?.discounted_payback_years == null ? "—" : `${number(s.headline.discounted_payback_years, 1)} ${tr("έτη", "years")}`, "payback"))}</TableRow>
                <TableRow><TableCell>{coverageLabel}</TableCell>{system.map((s) => cell(s, s.metrics?.self_sufficiency_pct == null ? "—" : `${number(s.metrics.self_sufficiency_pct, 1)}%`, "self_sufficiency"))}</TableRow>
                <TableRow><TableCell />{system.map((s) => (
                  <TableCell key={s.key} align="right">
                    {s.is_study || s.battery === studyName ? null : (
                      <Button size="small" disabled={disabled || studyRunning} onClick={() => adopt(s)}>{tr("Χρήση στη μελέτη", "Use in the study")}</Button>
                    )}
                  </TableCell>
                ))}</TableRow>
              </TableBody>
            </Table>
            {alone.length ? (
              <>
                <Typography variant="subtitle2" component="h4" sx={{ mt: 2 }}>{tr("Μόνο μπαταρία, χωρίς Φ/Β", "Battery only, without PV")}</Typography>
                <Table size="small">
                  {head(alone)}
                  <TableBody>
                    <TableRow><TableCell>NPV</TableCell>{alone.map((s) => cell(s, money(s.headline?.npv_eur)))}</TableRow>
                    <TableRow><TableCell>{tr("Προεξοφλημένη απόσβεση", "Discounted payback")}</TableCell>{alone.map((s) => cell(s, s.headline?.discounted_payback_years == null ? "—" : `${number(s.headline.discounted_payback_years, 1)} ${tr("έτη", "years")}`))}</TableRow>
                  </TableBody>
                </Table>
              </>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={download} disabled={Boolean(busy)}
            startIcon={busy === "pdf" ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}>
            {tr("Λήψη συγκριτικού PDF", "Download comparison PDF")}
          </Button>
          <Button variant="contained" onClick={() => setResultsOpen(false)}>{tr("Κλείσιμο", "Close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
